import { Link, useRouterState } from "@tanstack/react-router";
import { SERVICES } from "@/lib/services-data";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Phone, MessageSquare, ChevronDown } from "lucide-react";

export const PHONE = "406-799-0095";
export const TEL = "tel:4067990095";
export const SMS = "sms:4067990095";
export const EMAIL = "joshuaginnaty@icloud.com";
export const GBP = "https://share.google/hX4xEP1AaYC2ZP5Bl";

export const NAV = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/fleet-service", label: "Fleet Service" },
  { to: "/about", label: "About" },
  { to: "/service-area", label: "Service Area" },
  { to: "/reviews", label: "Reviews" },
  { to: "/contact", label: "Contact" },
] as const;

const FOOTER_PAGES = [...NAV, { to: "/faq", label: "FAQ" }] as const;

/** Text wordmark — swap this component's contents for an <img> logo later. */
export function Wordmark({ className = "" }: { className?: string }) {
  return (
    <span className={`font-display text-2xl font-black uppercase leading-none tracking-wide ${className}`}>
      <span className="text-primary">406</span> Mobile Mechanic
    </span>
  );
}

export function Reveal({ children, className = "", as: Tag = "div" }: { children: ReactNode; className?: string; as?: "div" | "li" | "article" }) {
  const ref = useRef<HTMLElement>(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => { if (e?.isIntersecting) { setShown(true); io.disconnect(); } }, { threshold: 0.12 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    // @ts-expect-error polymorphic ref
    <Tag ref={ref} className={`reveal ${shown ? "reveal-in" : ""} ${className}`}>{children}</Tag>
  );
}

export function CallButton({ label = `Call ${PHONE}`, size = "md" }: { label?: string; size?: "md" | "lg" }) {
  const s = size === "lg" ? "px-7 py-4 text-xl" : "px-4 py-2 text-base";
  return (
    <a href={TEL} className={`inline-flex items-center gap-2 rounded-sm bg-primary font-display font-extrabold uppercase tracking-wide text-primary-foreground transition-transform hover:-translate-y-0.5 ${s}`}>
      <Phone className="h-4 w-4" aria-hidden /> {label}
    </a>
  );
}
export function TextButton({ label = "Text Us", size = "md" }: { label?: string; size?: "md" | "lg" }) {
  const s = size === "lg" ? "px-7 py-4 text-xl" : "px-4 py-2 text-base";
  return (
    <a href={SMS} className={`inline-flex items-center gap-2 rounded-sm border-2 border-primary font-display font-extrabold uppercase tracking-wide text-primary transition-colors hover:bg-primary hover:text-primary-foreground ${s}`}>
      <MessageSquare className="h-4 w-4" aria-hidden /> {label}
    </a>
  );
}

const navCls = "text-sm font-semibold uppercase tracking-wider text-muted-foreground transition-colors hover:text-primary";
const activeCls = { className: "text-primary" };

function ServicesMenu() {
  const [open, setOpen] = useState(false);
  const path = useRouterState({ select: (s) => s.location.pathname });
  useEffect(() => setOpen(false), [path]);
  return (
    <div className="group relative" onMouseLeave={() => setOpen(false)}>
      <div className="flex items-center gap-1">
        <Link to="/services" className={navCls} activeProps={activeCls}>Services</Link>
        <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} aria-controls="services-menu" aria-label="Show all services" className="text-muted-foreground hover:text-primary">
          <ChevronDown className="h-4 w-4" aria-hidden />
        </button>
      </div>
      <div id="services-menu" className={`absolute left-1/2 top-full z-50 w-[34rem] -translate-x-1/2 pt-3 group-hover:block group-focus-within:block ${open ? "block" : "hidden"}`}>
        <ul className="grid grid-cols-2 gap-px overflow-hidden rounded-sm border border-border bg-border shadow-2xl">
          {SERVICES.map((sv) => (
            <li key={sv.slug}>
              <Link to="/services/$slug" params={{ slug: sv.slug }} className="flex h-full items-center gap-2 bg-card px-4 py-3 text-sm font-semibold hover:text-primary" activeProps={activeCls}>
                <sv.icon className="h-4 w-4 shrink-0 text-primary" aria-hidden />{sv.name}
              </Link>
            </li>
          ))}
          <li className="col-span-2">
            <Link to="/services" activeOptions={{ exact: true }} className="label-mono block bg-charcoal-deep px-4 py-3 text-primary hover:underline">All Services →</Link>
          </li>
        </ul>
      </div>
    </div>
  );
}

export function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [svcOpen, setSvcOpen] = useState(false);
  const path = useRouterState({ select: (s) => s.location.pathname });
  useEffect(() => { setOpen(false); setSvcOpen(false); }, [path]);
  return (
    <>
      <header className="sticky top-0 z-50 border-b border-border bg-charcoal-deep/95 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 sm:px-6">
          <Link to="/" aria-label="406 Mobile Mechanic home"><Wordmark /></Link>
          <nav aria-label="Main" className="hidden items-center gap-4 xl:flex">
            {NAV.map((n) => n.to === "/services" ? <ServicesMenu key={n.to} /> : (
              <Link key={n.to} to={n.to} className={navCls} activeProps={activeCls} activeOptions={{ exact: n.to === "/" }}>{n.label}</Link>
            ))}
          </nav>
          <div className="hidden items-center gap-2 md:flex">
            <TextButton />
            <CallButton />
          </div>
          <button type="button" onClick={() => setOpen(!open)} className="rounded-sm border border-border px-3 py-2 text-sm font-bold uppercase xl:hidden" aria-expanded={open} aria-controls="mobile-nav" aria-label={open ? "Close menu" : "Open menu"}>
            {open ? "Close" : "Menu"}
          </button>
        </div>
        {open && (
          <nav id="mobile-nav" aria-label="Mobile" className="max-h-[75vh] overflow-y-auto border-t border-border px-4 pb-4 xl:hidden">
            {NAV.map((n) => n.to === "/services" ? (
              <div key={n.to} className="border-b border-border">
                <div className="flex items-center justify-between">
                  <Link to="/services" activeOptions={{ exact: true }} activeProps={activeCls} className="block py-3 font-display text-xl font-bold uppercase">Services</Link>
                  <button type="button" onClick={() => setSvcOpen(!svcOpen)} aria-expanded={svcOpen} aria-controls="mobile-services" aria-label="Show all services" className="p-2 text-primary">
                    <ChevronDown className={`h-6 w-6 transition-transform ${svcOpen ? "rotate-180" : ""}`} aria-hidden />
                  </button>
                </div>
                {svcOpen && (
                  <ul id="mobile-services" className="pb-3 pl-3">
                    {SERVICES.map((sv) => (
                      <li key={sv.slug}><Link to="/services/$slug" params={{ slug: sv.slug }} activeProps={activeCls} className="block py-2 font-semibold">{sv.name}</Link></li>
                    ))}
                    <li><Link to="/services" activeOptions={{ exact: true }} className="label-mono block py-2 text-primary">All Services →</Link></li>
                  </ul>
                )}
              </div>
            ) : (
              <Link key={n.to} to={n.to} activeProps={activeCls} activeOptions={{ exact: n.to === "/" }} className="block border-b border-border py-3 font-display text-xl font-bold uppercase">{n.label}</Link>
            ))}
          </nav>
        )}
      </header>
      {/* Mobile sticky call/text bar */}
      <div className="fixed inset-x-0 bottom-0 z-50 grid grid-cols-2 border-t-2 border-primary bg-charcoal-deep md:hidden">
        <a href={TEL} aria-label={`Call ${PHONE}`} className="flex items-center justify-center gap-2 bg-primary py-4 font-display text-2xl font-black uppercase text-primary-foreground"><Phone className="h-5 w-5" aria-hidden /> Call</a>
        <a href={SMS} aria-label={`Text ${PHONE}`} className="flex items-center justify-center gap-2 py-4 font-display text-2xl font-black uppercase text-primary"><MessageSquare className="h-5 w-5" aria-hidden /> Text</a>
      </div>
    </>
  );
}

export function SiteFooter() {
  return (
    <footer className="topo border-t-2 border-primary bg-charcoal-deep pb-20 md:pb-0">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:px-6 md:grid-cols-2 lg:grid-cols-5">
        <div>
          <Wordmark className="text-3xl" />
          <p className="label-mono mt-3 text-steel">Est. 2020 · Great Falls, MT</p>
          <p className="mt-4 text-sm text-muted-foreground">Your mobile mechanic in Great Falls, MT. Owner Josh Ginnaty brings auto repair, diagnostics and roadside help to your home, job site, or roadside.</p>
        </div>
        <address className="not-italic text-sm leading-7">
          <p className="label-mono mb-2 text-primary">Contact</p>
          <a href={TEL} className="block hover:text-primary">Call: {PHONE}</a>
          <a href={SMS} className="block hover:text-primary">Text: {PHONE}</a>
          <a href={`mailto:${EMAIL}`} className="block break-all hover:text-primary">{EMAIL}</a>
          <a href={GBP} target="_blank" rel="noopener" className="block hover:text-primary">Google reviews →</a>
        </address>
        <div className="text-sm leading-7">
          <p className="label-mono mb-2 text-primary">Hours & Area</p>
          <p>Mon–Sun · 8 AM – 9 PM</p>
          <Link to="/service-area" className="block text-muted-foreground hover:text-primary">Great Falls, Black Eagle, Malmstrom AFB, Vaughn, Belt, Cascade, Ulm, Sun River, Dutton, Fort Benton & statewide travel.</Link>
        </div>
        <nav aria-label="Footer" className="text-sm leading-7">
          <p className="label-mono mb-2 text-primary">Pages</p>
          {FOOTER_PAGES.map((n) => <Link key={n.to} to={n.to} className="block hover:text-primary">{n.label}</Link>)}
        </nav>
        <nav aria-label="Footer services" className="text-sm leading-7">
          <p className="label-mono mb-2 text-primary">Services</p>
          {SERVICES.map((sv) => <Link key={sv.slug} to="/services/$slug" params={{ slug: sv.slug }} className="block hover:text-primary">{sv.name}</Link>)}
        </nav>
      </div>
      <p className="label-mono border-t border-border py-4 text-center text-muted-foreground">© {new Date().getFullYear()} 406 Mobile Mechanic · Est. 2020</p>
    </footer>
  );
}

export function ComingSoon({ title }: { title: string }) {
  return (
    <main className="topo flex min-h-[60vh] items-center bg-background px-4">
      <div className="mx-auto max-w-2xl py-24 text-center">
        <p className="label-mono text-primary">Coming soon</p>
        <h1 className="mt-3 text-6xl font-black sm:text-7xl">{title}</h1>
        <p className="mt-4 text-muted-foreground">This page is on the way. Need help now? Call or text Josh.</p>
        <div className="mt-8 flex flex-wrap justify-center gap-3"><CallButton size="lg" /><TextButton size="lg" label="Text Josh" /></div>
      </div>
    </main>
  );
}
